import { useMemo } from 'react';

const useCitationLogic = ({ citation, citationText, fullReferenceText, multipleCitations }) => {
  // Helper function to process a single citation
  const processCitation = (text, fullText) => {
    const referenceText = fullText || text || '';
    
    // Extract URLs from the reference text
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = referenceText.match(urlRegex) || [];
    
    // Check if there's a PubMed URL
    const pubmedUrl = urls.find(url => 
      url.includes('pubmed.ncbi.nlm.nih.gov') || url.includes('ncbi.nlm.nih.gov')
    );
    
    // Get fallback URL (first non-PubMed URL, but only if no PubMed URL exists)
    const fallbackUrl = pubmedUrl ? null : (urls.find(url => 
      !url.includes('pubmed.ncbi.nlm.nih.gov') && !url.includes('ncbi.nlm.nih.gov')
    ) || null);
    
    // Remove URLs from display text
    const displayText = referenceText.replace(urlRegex, '').trim();
    
    return {
      displayText,
      hasPubMed: !!pubmedUrl,
      pubmedUrl,
      fallbackUrl,
      urls
    };
  };

  // Process multiple citations if provided
  const processedMultipleCitations = useMemo(() => {
    if (!multipleCitations || !Array.isArray(multipleCitations)) {
      return null;
    }
    
    return multipleCitations.map((citationItem, index) => {
      const citationData = processCitation(citationItem.text, citationItem.fullReference);
      
      return {
        ...citationItem,
        ...citationData,
        handlePubMedClick: () => {
          if (citationData.pubmedUrl) {
            window.open(citationData.pubmedUrl, '_blank');
          }
        },
        handleUrlClick: () => {
          if (citationData.fallbackUrl) {
            window.open(citationData.fallbackUrl, '_blank');
          }
        }
      };
    });
  }, [multipleCitations]);
  
  const actualCitationText = citationText || citation?.citationText || '';
  const actualFullReferenceText = fullReferenceText || citation?.fullReferenceText || '';
  
  const singleCitationData = useMemo(() => {
    return processCitation(actualCitationText, actualFullReferenceText);
  }, [actualCitationText, actualFullReferenceText]);

  // Handler for PubMed clicks (single citation)
  const handlePubMedClick = () => {
    if (singleCitationData.pubmedUrl) {
      window.open(singleCitationData.pubmedUrl, '_blank');
    }
  };

  // Handler for URL clicks (single citation)
  const handleUrlClick = () => {
    if (singleCitationData.fallbackUrl) {
      window.open(singleCitationData.fallbackUrl, '_blank');
    }
  };

  return {    
    ...singleCitationData,
    handlePubMedClick,
    handleUrlClick,    
    multipleCitations: processedMultipleCitations,
    hasMultipleCitations: !!processedMultipleCitations
  };
};

export default useCitationLogic;